import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';
import ContentLoader from 'react-content-loader';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import Locator from '../../../shared/Locator';
import type { Package } from '../../../shared/organizationPackages';
import * as PackageInventoryStore from '../../redux/packageInventory';
import { Badge, BadgeColors, icons } from '../generic';
import { BadgeShapes } from '../generic/Badge/Badge';
import FetcherIcon from '../generic/FetcherIcon';
import Flex from '../generic/Flex';
import List from '../generic/List';

//  Note that we don't use the List.RowLoading here since this loader needs to look a bit different.
const PackageInventorySidebarRowLoading = (
  <List.RowContainer>
    <List.Row testId="package-inventory-loading-row" hasTightPadding>
      <ContentLoader width="100%" height="40px">
        <rect x="0" y="5" rx="3" ry="3" width="50%" height="14" />
        <rect x="0" y="32" rx="3" ry="3" width="12%" height="8" />
        <rect x="14%" y="32" rx="3" ry="3" width="25%" height="8" />
        <rect x="42%" y="32" rx="3" ry="3" width="12%" height="8" />
      </ContentLoader>
    </List.Row>
  </List.RowContainer>
);

type PackageInventorySidebarRowProps = {
  packageSummary: Package;
  params: Partial<{
    packageLocator: string;
  }>;
};

const PackageInventorySidebarRow = withRouter<PackageInventorySidebarRowProps>(({ packageSummary, params }) => {
  const dispatch = useDispatch();
  const isCurrentRowSelected = params?.packageLocator === packageSummary.locator;
  const stateQueryString = useSelector(PackageInventoryStore.selectors.getPackagesStateQueryString);
  const { fetcher } = Locator.parse(packageSummary.locator);
  return (
    <List.RowContainer>
      <List.Row
        to={{
          pathname: `/packages/${encodeURIComponent(packageSummary.locator)}`,
          search: `?${stateQueryString}`,
        }}
        isSelected={isCurrentRowSelected}
        onClick={() => {
          if (!isCurrentRowSelected) {
            dispatch(PackageInventoryStore.actions.resetPackageDetails());
          }
        }}
        testId="package-inventory-sidebar-item"
        hasTightPadding
    >
        <List.RowTitle>{packageSummary.title}</List.RowTitle>
        <List.RowSubtitle>{packageSummary.locator}</List.RowSubtitle>
        <List.RowDetails>
          <List.RowDetailsItem>
            <FetcherIcon fetcher={fetcher || ''} />
          </List.RowDetailsItem>
          <List.RowDetailsIconItem icon={icons.folders} count={packageSummary.numberOfProjects}>
            Projects
          </List.RowDetailsIconItem>
        </List.RowDetails>
      </List.Row>
    </List.RowContainer>
  );
});

/**
 * Renders the sidebar where the list of packages are displayed.
 */
const PackageInventorySidebar: VoidFunctionComponent = () => {
  const packages = useSelector(PackageInventoryStore.selectors.getPackages);
  const { isLoading } = useSelector(PackageInventoryStore.selectors.getAsyncStatusGetPackages);
  const count = useSelector(PackageInventoryStore.selectors.getPackagesCount);
  const totalItems = useSelector(PackageInventoryStore.selectors.getTotalPackagesCount);

  return (
    <div className="package-inventory__sidebar">
      <List.SidebarHeader>
        <List.HeaderText>
          <Flex justify="space-between">
            <Flex justify="flex-start">
              <span className="margin-right-xs">Packages</span>
              <Badge shape={BadgeShapes.Circle} color={BadgeColors.LightGray}>
                {isLoading ? '...' : totalItems.toString()}
              </Badge>
            </Flex>
          </Flex>
        </List.HeaderText>
      </List.SidebarHeader>
      <List.Body hasOverflowAuto>
        {isLoading
          ? _.times(count, () => PackageInventorySidebarRowLoading)
          : packages.map((p) => <PackageInventorySidebarRow key={p.locator} packageSummary={p} />)}
      </List.Body>
    </div>
  );
};

export default PackageInventorySidebar;
